import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import ExpandMore from '@mui/icons-material/ExpandMore';

import { DateTime } from 'luxon';

import { useEffect, useState } from 'react';

import { addEventEarlyAccess, getEventEarlyAccess, removeEventEarlyAccess } from '../../api.js';
import { useAuth } from '../../AuthContext';
import { AddPatrollersDialog } from './AddPatrollersDialog';

const fmt = (ms) => DateTime.fromMillis(Number(ms)).toFormat('LLL d, h:mm a');
const has = (v) => v !== null && v !== undefined;

const Section = ({ title, kind, grants, endsText, disabledText, addDisabled, onAdd, onRemove, busy }) => (
    <div style={ { borderTop: '1px solid rgba(128,128,128,.3)', paddingTop: 12, marginBottom: 12, opacity: disabledText ? 0.6 : 1 } }>
        <div style={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 } }>
            <Typography>
                <b>{ title }</b> <span style={ { opacity: 0.7 } }>- { disabledText || endsText }</span>
            </Typography>

            <Button size="small" variant="outlined" data-testid={ `add-${ kind }` } disabled={ addDisabled || busy } onClick={ () => onAdd(kind) }>
                + Add patrollers
            </Button>
        </div>

        <div style={ { marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 } }>
            { grants.map(g => (
                <Chip
                    key={ g.userId }
                    data-testid={ `grant-${ g.userId }` }
                    size="small"
                    variant="outlined"
                    label={ g.ended ? `${ g.name } (ended)` : g.name }
                    sx={ g.ended ? { opacity: 0.6 } : undefined }
                    onDelete={ busy ? undefined : () => onRemove(kind, g.userId) }
                />
            )) }
        </div>
    </div>
);

export const EarlyAccessCard = ({ eventId, isAdmin }) => {
    const { token } = useAuth();

    const [ data, setData ] = useState(null);
    const [ error, setError ] = useState('');
    const [ busy, setBusy ] = useState(false);
    const [ addKind, setAddKind ] = useState(null);
    const [ forbidden, setForbidden ] = useState(false);

    const load = async () => {
        const result = await getEventEarlyAccess(eventId, token);

        if (result?.forbidden) {
            setForbidden(true);
        } else if (result?.error) {
            setError(result.error);
        } else {
            setError('');
            setData(result);
        }
    };

    // One load per event. A failure shows an alert and stops; nothing retries.
    useEffect(() => {
        if (!isAdmin || !eventId) return;

        load();
    }, [ isAdmin, eventId ]);

    if (!isAdmin || !eventId || forbidden) return null;

    const handleRemove = async (kind, userId) => {
        setBusy(true);

        const result = await removeEventEarlyAccess(eventId, kind, userId, token);

        if (result?.error) {
            setError(result.error);
        } else {
            await load();
        }
        setBusy(false);
    };

    const handleAdd = async (userIds) => {
        const result = await addEventEarlyAccess(eventId, addKind, userIds, token);

        if (result?.error) return result;

        setAddKind(null);
        await load();

        return result;
    };

    // Section end times and usability come only from the server (sections.<kind>);
    // the card never derives them from the raw dates.
    const dates = data?.dates || {};
    const sections = data?.sections || {};
    const priorityOk = sections.priority?.effective === true;
    const earlyOk = sections.early?.effective === true;
    const noDates = !priorityOk && !earlyOk;
    const priority = data?.priority || [];
    const early = data?.early || [];

    const orderParts = [ 'Priority' ];
    if (has(dates.earlyOpen)) orderParts.push(`Early sign-up (opens ${ fmt(dates.earlyOpen) })`);
    if (has(dates.opens)) orderParts.push(`General (opens ${ fmt(dates.opens) })`);

    const endsText = (section) => (has(section?.endsAt) ? `until ${ fmt(section.endsAt) }` : 'no effect without dates');
    const priorityEnds = endsText(sections.priority);
    const earlyEnds = endsText(sections.early);

    return (
        <>
            { error && <Alert severity="error" sx={ { mt: 2 } } onClose={ () => setError('') }>{ error }</Alert> }

            <Accordion disableGutters slotProps={ { transition: { unmountOnExit: true } } } sx={ { my: 2 } }>
                <AccordionSummary expandIcon={ <ExpandMore /> } data-testid="early-access-summary">
                    <div>
                        <Typography sx={ { fontWeight: 600 } }>Early Access</Typography>

                        { data && (
                            <Typography variant="body2" sx={ { opacity: 0.8 } }>
                                Priority: { priority.length } patrollers | Early sign-up: { early.length } patrollers
                            </Typography>
                        ) }
                    </div>
                </AccordionSummary>

                <AccordionDetails>
                    { data && (
                        <>
                            { noDates ? (
                                <Alert severity="warning" sx={ { mb: 1 } }>
                                    Set early sign-up or general registration dates first. Early access has no effect without them.
                                </Alert>
                            ) : (
                                <Typography variant="body2" sx={ { opacity: 0.75, mb: 2 } }>Order: { orderParts.join(' > ') }</Typography>
                            ) }

                            <Section
                                title="Priority access"
                                kind="priority"
                                grants={ priority }
                                endsText={ priorityEnds }
                                addDisabled={ !priorityOk }
                                busy={ busy }
                                onAdd={ setAddKind }
                                onRemove={ handleRemove }
                            />

                            <Section
                                title="Early sign-up"
                                kind="early"
                                grants={ early }
                                endsText={ earlyEnds }
                                disabledText={ !noDates && !earlyOk ? 'set an early sign-up date to use this' : '' }
                                addDisabled={ !earlyOk }
                                busy={ busy }
                                onAdd={ setAddKind }
                                onRemove={ handleRemove }
                            />
                        </>
                    ) }
                </AccordionDetails>

                { addKind && (
                    <AddPatrollersDialog
                        title={ `Add to ${ addKind === 'priority' ? 'Priority access' : 'Early sign-up' }` }
                        excludeIds={ (addKind === 'priority' ? priority : early).map(g => g.userId) }
                        onAdd={ handleAdd }
                        onClose={ () => setAddKind(null) }
                    />
                ) }
            </Accordion>
        </>
    );
};
